import { PickerPopover } from '../PickerPopover';
import { useTranslate } from '@tolgee/react';
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Empty, message, Pagination, Spin, Tooltip } from 'antd';
import { queries } from 'services/queries';
import { SearchInput } from 'components/LightConversation/components/SearchInput';
import { ProductListParamsKeywordType } from 'services/api/conversation';
import SyncItem from 'views/Conversation/Sider/SyncItem';
import { ProductPickerSort, OrderBy } from './Sort';
import { ProductItem } from './ProductItem';
import useIM from 'hooks/business/im/useIM';
import { ProductListSkeleton } from './ProductListSkeleton';
import { ToolbarIcon } from '../../ToolbarIcon';
import { css, cx } from '@emotion/css';

interface ProductPickerProps {
  conversation: Conversation.Conversation;
}

export const ProductPicker = (props: ProductPickerProps) => {
  const { conversation } = props;
  const { conversationId, storeId, channel } = conversation;
  const { t } = useTranslate();

  const [visible, setVisible] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [keywordType, setKeywordType] = useState<ProductListParamsKeywordType>(
    ProductListParamsKeywordType.ITEM_NAME,
  );
  const [keyword, setKeyword] = useState('');
  const [orderBy, setOrderBy] = useState<OrderBy>('RECOMMEND');
  const { sendItem } = useIM();

  const params = useMemo(() => {
    const sortStrategy = orderBy === 'RECOMMEND' ? 'CATEGORY_PRIORITY' : undefined;
    const order_by = orderBy !== 'RECOMMEND' ? orderBy : undefined;
    return {
      internal_store_id: storeId,
      page_no: pageNo,
      page_size: pageSize,
      keyword,
      keyword_type: keywordType,
      order_by,
      sortStrategy,
    };
  }, [storeId, pageNo, pageSize, keyword, keywordType, orderBy]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    ...queries.products.list(params),
    keepPreviousData: true,
    enabled: visible,
  });

  const handleSelectChange = useCallback((value: string) => {
    setKeywordType(value as unknown as ProductListParamsKeywordType);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setKeyword(value);
    setPageNo(1);
  }, []);

  const handlePageChange = useCallback((page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  }, []);

  const searchPlaceholder = useMemo(() => {
    return {
      [ProductListParamsKeywordType.ITEM_NAME]: t(
        'scene.chatWorkspace.productTab.search.placeholder.name',
      ),
      [ProductListParamsKeywordType.ITEM_ID]: t(
        'scene.chatWorkspace.productTab.search.placeholder.id',
      ),
      [ProductListParamsKeywordType.SKU]: t(
        'scene.chatWorkspace.productTab.search.placeholder.sku',
      ),
      [ProductListParamsKeywordType.ITEM_NO]: t(
        'scene.chatWorkspace.productTab.search.placeholder.skuCode',
      ),
    }[keywordType];
  }, [keywordType, t]);

  const searchOptions: { value: string; label: string }[] = useMemo(
    () => [
      {
        value: ProductListParamsKeywordType.ITEM_NAME as unknown as string,
        label: t('domain.product.field.name'),
      },
      {
        value: ProductListParamsKeywordType.ITEM_ID as unknown as string,
        label: t('domain.product.field.id'),
      },
      { value: ProductListParamsKeywordType.SKU as unknown as string, label: 'SKU' },
      {
        value: ProductListParamsKeywordType.ITEM_NO as unknown as string,
        label: t('domain.product.field.skuCode'),
      },
    ],
    [t],
  );

  const handleSend = useCallback(
    (item: Conversation.Product) => {
      const { externalShopId, externalItemId } = item;
      sendItem(conversationId, {
        externalStoreId: externalShopId,
        externalItemId,
      }).catch(() => {
        message.error(t('scene.chatWorkspace.productTab.feedback.sendFailed'));
      });
    },
    [conversationId, sendItem, t],
  );

  let content = null;

  const isEmpty = data?.itemList?.length === 0;

  if (isLoading) {
    content = <ProductListSkeleton />;
  } else if (isEmpty) {
    content = (
      <Empty
        className="pt-4"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('scene.chatWorkspace.productTab.empty.default')}
      />
    );
  } else {
    content = (
      <div className="flex-1 overflow-y-auto px-4 grid grid-cols-2 gap-3 items-start">
        {data?.itemList?.map((item) => (
          <ProductItem
            key={item.internalItemId}
            item={item}
            onSend={handleSend}
            platform={channel as General.PlatformUpperCase}
          />
        ))}
      </div>
    );
  }

  return (
    <PickerPopover
      title={t('domain.product.field.product')}
      icon={
        <Tooltip title={t('domain.product.field.product')}>
          <ToolbarIcon icon="product" />
        </Tooltip>
      }
      headerExtra={<SyncItem internalStoreId={storeId} onSuccess={refetch} />}
      onVisibleChange={setVisible}
    >
      <div className={cx('flex flex-col w-[1000px] min-h-[280px] max-h-[60vh]', containerCls)}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="max-w-[320px]">
            <SearchInput
              placeholder={searchPlaceholder}
              selectOptions={searchOptions}
              selectValue={keywordType as unknown as string}
              onSelectChange={handleSelectChange}
              onSearch={handleSearch}
            />
          </div>
          <ProductPickerSort channel={channel} orderBy={orderBy} onChange={setOrderBy} />
        </div>

        <Spin spinning={isFetching}>{content}</Spin>

        {!isEmpty && (
          <div className="flex justify-end px-4 pb-4 pt-2">
            <Pagination
              size="small"
              total={data?.pageInfo?.totalCount || 0}
              pageSize={pageSize}
              current={pageNo}
              onChange={handlePageChange}
              showSizeChanger={false}
              showQuickJumper={false}
            />
          </div>
        )}
      </div>
    </PickerPopover>
  );
};

const containerCls = css`
  .ant-spin-nested-loading {
    overflow-y: auto;
  }
`;
